import type {
  ActionWithNullableValue,
  StoreProduct,
} from "replo-runtime/shared/types";
import type { ProductRefOrDynamic } from "schemas/product";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import {
  selectAllProducts,
  selectProductData,
  setProductData,
} from "@v1/reducers/commerce-reducer";
import {
  selectIsShopifyIntegrationEnabled,
  selectProductsIdsFromDraftElement,
} from "@v1/reducers/core-reducer";
import { useEditorSelector, useEditorStore } from "@v1/store/store";
import { mapProductToStoreProduct } from "@v1/utils/product";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { productRouter } from "@v2/trpc/routes/product";
import unionWith from "lodash-es/unionWith";
import uniq from "lodash-es/uniq";
import { shallowEqual } from "react-redux";
import {
  getCurrentComponentContext,
  resolveContextValue,
} from "replo-runtime/shared/utils/context";
import { isContextRef } from "replo-runtime/store/ReploProduct";
import { filterNulls } from "replo-utils/lib/array";
import {
  exhaustiveSwitch,
  isNotNullish,
  isNullish,
} from "replo-utils/lib/misc";

export const storeProductRequestLimits = {
  // Note (Noah, 2025-05-29): If we have the write-through sync enabled, we'll sync from storefront
  // api, so we can load a lot more products at a time
  infiniteLoadingWriteThroughProductsSync: 100,
  infiniteLoadingShopifyAdminApi: 5,
  defaultForAnyProducts: 5,
};

const resolveProductRef = (
  value: ProductRefOrDynamic | null | undefined,
  draftComponentId: string,
) => {
  if (isNullish(value)) {
    return null;
  }
  const productRef = resolveContextValue(
    value,
    // Note (Noah, 2022-08-04): The repeated index 0 here is okay because
    // if we're inside something dynamic over products, we'll only be
    // selecting a variant via dynamic data anyways. These productIds are
    // only used for selecting variants right now.
    getCurrentComponentContext(draftComponentId),
  );

  return productRef;
};

const productIdsFromContextRef = (
  value: ProductRefOrDynamic | null | undefined,
  draftComponentId: string,
) => {
  const productRef =
    isNotNullish(value) && isContextRef(value)
      ? resolveProductRef(value, draftComponentId)
      : value;
  return productRef ? [Number(productRef.productId)] : [];
};

const productIdsFromPartialAction = (
  draftAction: ActionWithNullableValue,
  draftComponentId: string,
) => {
  return exhaustiveSwitch(draftAction)({
    addProductVariantToCart: (action) => {
      return productIdsFromContextRef(action.value?.product, draftComponentId);
    },
    updateCurrentProduct: (action) => {
      return productIdsFromContextRef(action.value, draftComponentId);
    },
    redirect: [],
    clearCart: [],
    redirectToProductPage: (action) => {
      const productRefFromAction =
        action.value && "product" in action.value
          ? action.value.product
          : action.value;
      return productIdsFromContextRef(productRefFromAction, draftComponentId);
    },
    applyDiscountCode: [],
    clearDiscountCode: [],
    updateCart: (action) => {
      return productIdsFromContextRef(action.value, draftComponentId);
    },
    multipleProductVariantsAddToCart: (action) => {
      const productIds = action.value?.flatMap(({ product }) => {
        return productIdsFromContextRef(product, draftComponentId);
      });
      return productIds ?? [];
    },
    close: [],
    closeModalComponent: [],
    scrollContainerLeft: [],
    scrollContainerRight: [],
    scrollToPreviousCarouselItem: [],
    scrollToNextCarouselItem: [],
    scrollToUrlHashmark: [],
    setCurrentCollectionSelection: [],
    scrollToSpecificCarouselItem: [],
    addTemporaryCartProductsToCart: [],
    openModal: [],
    openKlaviyoModal: [],
    removeVariantFromTemporaryCart: [],
    setSelectedListItem: [],
    toggleCollapsible: [],
    toggleDropdown: [],
    toggleMute: [],
    togglePlay: [],
    toggleFullScreen: [],
    setActiveAlchemyVariant: [],
    setDropdownItem: [],
    setActiveOptionValue: [],
    addVariantToTemporaryCart: (action) => {
      return productIdsFromContextRef(action.value?.product, draftComponentId);
    },
    decreaseVariantCountInTemporaryCart: [],
    activateTabId: [],
    setActiveTabIndex: [],
    executeJavascript: [],
    increaseProductQuantity: [],
    decreaseProductQuantity: [],
    setProductQuantity: [],
    phoneNumber: [],
    setActiveVariant: [],
    goToItem: [],
    goToNextItem: [],
    goToPrevItem: [],
    setActiveSellingPlan: [],
  });
};

/**
 * Returns products we have cached in-memory for the given ids
 * @param ids Product ids to fetch
 */
export const useCachedProducts = (ids?: (string | number)[]) => {
  const productDataFromStore: Record<string, StoreProduct> =
    useEditorSelector(selectProductData);

  // NOTE (Chance 2023-06-28): This is a quick and dirty workaround to avoid the
  // need to memoize the incoming array of product ids unless one of its values
  // actually changes.
  let idsStringified: string | null = null;
  try {
    if (ids) {
      idsStringified = JSON.stringify(ids);
    }
  } catch {}

  const cachedProducts = React.useMemo((): StoreProduct[] => {
    if (!idsStringified) {
      return Object.values(productDataFromStore);
    }

    const productIds: number[] = JSON.parse(idsStringified);
    const uniqueIds = uniq(productIds);

    return filterNulls(uniqueIds.map((id) => productDataFromStore[String(id)]));
  }, [idsStringified, productDataFromStore]);

  return { cachedProducts };
};

/**
 * Hook which fetches the given products.
 *
 * @param query Specifies the type of products to fetch
 * @param forceSkip If true, request will never be executed (useful for hooks
 * where you) only sometimes want to actually fetch.
 */

export function useFetchStoreProducts(opts: {
  query:
    | { type: "any" }
    | { type: "specificExternalIds"; externalIds: number[] };
  forceSkip?: boolean;
}) {
  const { query, forceSkip } = opts;
  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const { cachedProducts } = useCachedProducts(
    query.type === "specificExternalIds" ? query.externalIds : undefined,
  );

  const remainingProductIds = React.useMemo(() => {
    if (query.type !== "specificExternalIds") {
      return undefined;
    }
    const cachedProductIds = new Set(cachedProducts.map((p) => String(p.id)));
    return query.externalIds.filter((id) => !cachedProductIds.has(String(id)));
  }, [query, cachedProducts]);

  const shouldSkip =
    forceSkip ||
    !isShopifyIntegrationEnabled ||
    !projectId ||
    (query.type === "specificExternalIds" && remainingProductIds?.length === 0);

  const isWritethroughProductsSyncEnabled =
    project?.hostingType === "replo" ||
    isFeatureEnabled("write-through-products-sync");
  const queryParams = shouldSkip
    ? skipToken
    : {
        projectId,
        limit: isWritethroughProductsSyncEnabled
          ? storeProductRequestLimits.infiniteLoadingWriteThroughProductsSync
          : storeProductRequestLimits.infiniteLoadingShopifyAdminApi,
        writeThroughProductsSync: isWritethroughProductsSyncEnabled,
        filters:
          query.type === "specificExternalIds"
            ? {
                externalIds: (remainingProductIds ?? query.externalIds).map(
                  String,
                ),
              }
            : { searchTerm: "" },
      };
  const { data, error, isLoading } = productRouter.list.useQuery(queryParams);
  const { dispatch } = useEditorStore();

  const products = React.useMemo((): StoreProduct[] => {
    if (!data?.products) {
      return cachedProducts;
    }

    const mappedProducts = data.products.map(mapProductToStoreProduct);
    return filterNulls([...cachedProducts, ...mappedProducts]);
  }, [cachedProducts, data?.products]);

  // Note (Kevin, 2025-06-20): Maintain Redux side effect for components that depend on product data store
  // This replicates RTK Query's automatic Redux updates on query success
  React.useEffect(() => {
    if (data?.products && data.products.length > 0) {
      const newProducts = products.filter((product) => {
        return !cachedProducts.some(
          (cached) => String(cached.id) === String(product.id),
        );
      });
      if (newProducts.length > 0) {
        dispatch(setProductData(newProducts));
      }
    }
  }, [data?.products, products, cachedProducts, dispatch]);

  return { products, isLoading, error };
}

/**
 * Hook which loads and returns a list of any of the products in the store.
 * Useful for when you just need to choose one default product.
 */
export function useAnyStoreProducts(config?: { forceSkip?: boolean }) {
  return useFetchStoreProducts({ query: { type: "any" }, ...config });
}

/**
 * Hook which loads and returns a list of all the products referenced by
 * components in the current draft element. Useful for passing into Repainter etc.
 */
export function useStoreProductsFromDraftElement(config?: {
  forceSkip?: boolean;
}) {
  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );
  const productIds = useDraftElementProductsIds();
  const draftElementProductsQueryData = useFetchStoreProducts({
    query: {
      type: "specificExternalIds",
      externalIds: productIds.map(Number),
    },
    forceSkip: (config?.forceSkip ?? false) || productIds.length === 0,
  });

  return {
    ...draftElementProductsQueryData,
    // Note (Martin, 2023-11-10): consider both possible queries loading
    isLoading:
      isShopifyIntegrationEnabled && draftElementProductsQueryData.isLoading,
  };
}

/**
 * Hook which loads and returns a list of any of the products in the store and
 * merge with the products in the draft element and all the products in the state.
 * Useful to grab as much products possible in the state for things like drop
 * component templates with specific products.
 */
export function useStoreAndDraftElementProducts(config?: {
  forceSkip?: boolean;
}) {
  const { products } = useAnyStoreProducts(config);
  const { products: productsFromDraftElement } =
    useStoreProductsFromDraftElement(config);
  const productsFromStore = useEditorSelector(selectAllProducts);

  return {
    products: unionWith(
      [...products, ...productsFromDraftElement, ...productsFromStore],
      (productOne, productTwo) => {
        return String(productOne.id) === String(productTwo.id);
      },
    ),
  };
}

export const useSpecificStoreProducts = (
  productIds: (string | number)[],
  config?: {
    forceSkip?: boolean;
  },
) => {
  return useFetchStoreProducts({
    query: {
      type: "specificExternalIds",
      externalIds: productIds.map(Number),
    },
    forceSkip: config?.forceSkip ?? false,
  });
};

export const useStoreProductsFromPartialAction = (
  action: ActionWithNullableValue | null,
  draftComponentId: string | null,
  config?: {
    forceSkip?: boolean;
  },
) => {
  const productIds =
    draftComponentId && action
      ? productIdsFromPartialAction(action, draftComponentId)
      : [];
  const specificStoreProducts = useSpecificStoreProducts(productIds, config);
  return {
    products: specificStoreProducts.products,
    isLoading: specificStoreProducts.isLoading,
    error: specificStoreProducts.error,
  };
};

/**
 * Hook which fetches and returns products from the current store, based on type.
 * Useful for times when you might want to fetch one type of products of a specific
 * type, based on a prop (only one request will be made by this hook, for the appropriate type)
 */

export type ProductRequestType = "anyProducts" | "productsFromDraftElement";
export const useStoreProducts = (type: ProductRequestType) => {
  const anyProductsQueryData = useAnyStoreProducts({
    forceSkip: type !== "anyProducts",
  });
  const draftElementProductsQueryData = useStoreProductsFromDraftElement({
    forceSkip: type !== "productsFromDraftElement",
  });
  return exhaustiveSwitch({ type })({
    anyProducts: anyProductsQueryData,
    productsFromDraftElement: draftElementProductsQueryData,
  });
};

export function useDraftElementProductsIds() {
  const productIds = useEditorSelector(
    selectProductsIdsFromDraftElement,
    // Note (Noah, 2022-01-13): Use shallowEqual because it's an array which
    // will change on every run of the selector
    shallowEqual,
  );

  return productIds;
}
